/* Cart drawer promo block: rotates the social proof line, counts down to the
   customer's local midnight and mirrors the discount message into the drawer's
   live region.

   The drawer header is re-rendered via innerHTML after every cart change, so
   this element is destroyed and recreated each time. Everything it owns is set
   up in connectedCallback and torn down in disconnectedCallback. The live
   region deliberately lives OUTSIDE the replaced subtree (in
   snippets/cart-drawer.liquid): a region inserted together with its own new
   text is not announced by most screen readers. */
if (!customElements.get('m-cart-promo')) {
  class MCartPromo extends HTMLElement {
    connectedCallback() {
      this.startRotator();
      this.startCountdown();
      this.announce();
    }

    disconnectedCallback() {
      (this.rotateTimers || []).forEach(clearInterval);
      this.rotateTimers = [];
      clearTimeout(this.countdownTimer);
      this.countdownTimer = null;
    }

    startRotator() {
      this.rotateTimers = [];

      this.querySelectorAll('.m-cart-promo__rotator').forEach((rotator) => {
        const slides = [...rotator.querySelectorAll('[data-slide]')];
        if (slides.length < 2) return;

        const interval = parseInt(rotator.dataset.rotateInterval, 10) || 3000;
        let index = slides.findIndex((slide) => slide.classList.contains('is-active'));
        if (index < 0) index = 0;

        this.rotateTimers.push(
          setInterval(() => {
            const leaving = slides[index];
            index = (index + 1) % slides.length;

            leaving.classList.remove('is-active');
            leaving.classList.add('is-above');
            slides[index].classList.add('is-active');

            /* Send the outgoing slide back below the line once it is out of
               sight, so its next entrance travels upwards like the first one. */
            setTimeout(() => leaving.classList.remove('is-above'), MCartPromo.TRANSITION_MS);
          }, interval)
        );
      });
    }

    /* Hours, minutes and seconds to the next local 00:00, read from the clock
       on every tick (a timer in a background tab is throttled, so counting
       ticks would drift), each tick just after a whole second so no number
       is skipped or shown twice. At midnight it starts on the next day. */
    startCountdown() {
      const hour = this.querySelector('.countdown-timer-hour');
      const min = this.querySelector('.countdown-timer-minute');
      const sec = this.querySelector('.countdown-timer-sec');
      if (!sec) return;

      const pad = (number) => (number < 10 ? `0${number}` : String(number));
      const tick = () => {
        const now = new Date();
        const midnight = new Date(now);
        midnight.setHours(24, 0, 0, 0);
        const left = midnight - now;
        const seconds = Math.floor(left / 1000);

        if (hour) hour.textContent = pad(Math.floor(seconds / 3600));
        if (min) min.textContent = pad(Math.floor((seconds % 3600) / 60));
        sec.textContent = pad(seconds % 60);

        this.countdownTimer = setTimeout(tick, (left % 1000) + 20);
      };
      tick();
    }

    /* Announce the discount message only when it actually changed, and never on
       the very first render - that would talk over the page load. */
    announce() {
      const region = document.getElementById(this.dataset.statusTarget);
      const message = this.dataset.message;
      if (!region || !message) return;

      if (region.dataset.seen === undefined) {
        region.dataset.seen = message;
        return;
      }
      if (region.dataset.seen === message) return;

      region.dataset.seen = message;
      region.textContent = message;
    }
  }

  /* Keep in step with the transition on .m-cart-promo__slide. */
  MCartPromo.TRANSITION_MS = 400;

  customElements.define('m-cart-promo', MCartPromo);
}
